import styled from 'styled-components';

// Form Section
export const FormSection = styled.section`
  margin-bottom: 15px;
  padding: 1.5rem;
  background-color: var(--bg-secondary, #2a2a2a);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

export const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary, #f0f0f0);
  margin-bottom: 1.5rem;
`;

// Form Groups
export const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  &.inline {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
  }
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-secondary, #aaa);
`;

// Input Fields
export const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color, #3a3a3a);
  border-radius: 4px;
  font-size: 1rem;
  background-color: var(--bg-primary, #1a1a1a);
  color: var(--text-primary, #f0f0f0);
  transition: border-color 0.2s;

  &::placeholder {
    color: var(--text-placeholder, #9CA3AF);
    opacity: 0.6;
  }

  &:focus {
    outline: none;
    border-color: var(--primary, #6366f1);
  }

  &:disabled {
    background-color: var(--bg-disabled, #2a2a2a);
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color, #3a3a3a);
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  min-height: 80px;
  background-color: var(--bg-primary, #1a1a1a);
  color: var(--text-primary, #f0f0f0);
  transition: border-color 0.2s;

  &::placeholder {
    color: var(--text-placeholder, #9CA3AF);
    opacity: 0.6;
  }

  &:focus {
    outline: none;
    border-color: var(--primary, #6366f1);
  }

  &:disabled {
    background-color: var(--bg-disabled, #2a2a2a);
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color, #3a3a3a);
  border-radius: 4px;
  font-size: 1rem;
  background-color: var(--bg-primary, #1a1a1a);
  color: var(--text-primary, #f0f0f0);
  transition: border-color 0.2s;

  &::placeholder {
    color: var(--text-placeholder, #9CA3AF);
    opacity: 0.6;
  }

  option:first-child {
    color: var(--text-placeholder, #9CA3AF);
    opacity: 0.6;
  }

  option {
    color: var(--text-primary);
    opacity: 1;
  }

  &:focus {
    outline: none;
    border-color: var(--primary, #6366f1);
  }

  &:disabled {
    background-color: var(--bg-disabled, #2a2a2a);
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

// Phone Input Group
export const PhoneInputGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const SearchButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: var(--primary, #6366f1);
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--primary-hover, #4f46e5);
  }

  &:disabled {
    background-color: var(--bg-disabled, #2a2a2a);
    cursor: not-allowed;
  }
`;

export const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// Loading and Error States
export const LoadingSpinner = styled.div`
  width: 1rem;
  height: 1rem;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const ErrorMessage = styled.div`
  color: var(--error, #ef4444);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

export const InfoMessage = styled.div`
  color: var(--text-secondary, #aaa);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

// Patient Info Card
export const PatientInfoCard = styled.div`
  background-color: var(--bg-primary, #1a1a1a);
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border: 1px solid var(--border-color, #3a3a3a);
`;

export const PatientInfoHeader = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary, #f0f0f0);
  margin-bottom: 1rem;
`;

export const PatientInfoContent = styled.div`
  display: grid;
  gap: 0.5rem;
`;

export const PatientInfoItem = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const PatientInfoLabel = styled.span`
  font-weight: 500;
  color: var(--text-secondary, #aaa);
`;

export const PatientInfoValue = styled.span`
  color: var(--text-primary, #f0f0f0);
`;

// Address Card
export const AddressCard = styled.div`
  background-color: var(--bg-primary, #1a1a1a);
  border-radius: 4px;
  padding: 1rem;
  margin-top: 1rem;
  border: 1px solid var(--border-color, #3a3a3a);
`;

export const AddressCardContent = styled.div`
  color: var(--text-primary, #f0f0f0);
`;

// Form Row
export const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

// Medication Items
export const MedicationItem = styled.div`
  background-color: var(--bg-primary, #1a1a1a);
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid var(--border-color, #3a3a3a);
`;

export const MedicationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

export const RemoveButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: var(--error, #ef4444);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--error-hover, #dc2626);
  }
`;

export const MedicineDetailDisplay = styled.div`
  background-color: var(--bg-secondary, #2a2a2a);
  border-radius: 4px;
  padding: 1rem;
  margin-top: 1rem;

  p {
    margin: 0.25rem 0;
    color: var(--text-secondary, #aaa);

    &.sku {
      font-weight: 500;
      color: var(--text-primary, #f0f0f0);
    }

    &.in-stock {
      color: var(--success, #10b981);
    }

    &.out-of-stock {
      color: var(--error, #ef4444);
    }
  }
`;

// Add More Button
export const AddMoreButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: var(--success, #10b981);
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--success-hover, #059669);
  }

  &:disabled {
    background-color: var(--bg-disabled, #2a2a2a);
    cursor: not-allowed;
  }
`;

// Order Summary
export const OrderSummary = styled.div`
  background-color: var(--bg-primary, #1a1a1a);
  border-radius: 4px;
  padding: 1.5rem;
  margin-top: 2rem;
  border: 1px solid var(--border-color, #3a3a3a);
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  color: var(--text-secondary, #aaa);

  &.total {
    font-weight: 600;
    color: var(--text-primary, #f0f0f0);
    border-top: 1px solid var(--border-color, #3a3a3a);
    margin-top: 0.5rem;
    padding-top: 1rem;
  }
`;

// Action Buttons
export const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

export const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.secondary ? `
    background-color: var(--bg-secondary, #2a2a2a);
    color: var(--text-secondary, #aaa);
    border: 1px solid var(--border-color, #3a3a3a);

    &:hover {
      background-color: var(--bg-hover, #3a3a3a);
    }
  ` : `
    background-color: var(--primary, #6366f1);
    color: white;
    border: none;

    &:hover {
      background-color: var(--primary-hover, #4f46e5);
    }
  `}

  &:disabled {
    background-color: var(--bg-disabled, #2a2a2a);
    cursor: not-allowed;
    opacity: 0.7;
  }
`; 